/**
 * Caps the SEO text of a collection page to a number of lines and reveals the rest on click.
 * The full text always stays in the markup, so search engines read all of it either way.
 */
class CollectionSeoText extends HTMLElement {
  #observer = new ResizeObserver(() => this.#update());

  /** @returns {HTMLElement} The element whose height is capped. */
  get #clamp() {
    return /** @type {HTMLElement} */ (this.querySelector('.collection-seo__text'));
  }

  /** @returns {HTMLElement} The rich text itself, always at its natural height. */
  get #body() {
    return /** @type {HTMLElement} */ (this.querySelector('.collection-seo__body'));
  }

  /** @returns {HTMLButtonElement} */
  get #toggle() {
    return /** @type {HTMLButtonElement} */ (this.querySelector('.collection-seo__toggle'));
  }

  connectedCallback() {
    this.#toggle.addEventListener('click', this.#onToggle);

    // Watching the body rather than the clamp covers viewport resizes, breakpoint changes and
    // late-loading fonts in one go, and the max-height we write on the clamp cannot feed back
    // into it. The observer fires once on observe, which doubles as the initial measurement.
    this.#observer.observe(this.#body);
  }

  disconnectedCallback() {
    this.#observer.disconnect();
  }

  #onToggle = () => {
    this.#toggle.setAttribute('aria-expanded', String(this.toggleAttribute('expanded')));
    this.#update();
  };

  #update() {
    const lines = parseFloat(getComputedStyle(this).getPropertyValue('--collection-seo-lines'));
    const collapsedHeight = lines * parseFloat(getComputedStyle(this.#body).lineHeight);

    this.#clamp.style.maxHeight = this.hasAttribute('expanded') ? '' : `${collapsedHeight}px`;
    this.#toggle.hidden = this.#body.offsetHeight <= collapsedHeight + 1;
  }
}

customElements.define('collection-seo-text', CollectionSeoText);
